/**
 * What the Android hardware back button does, as a pure decision so it can
 * be tested without a device:
 *  0. a screen with its own steps (onboarding) handles it first, via a
 *     cancelable `app:back` event on window
 *  1. an overlay opened over a screen closes (history back)
 *  2. otherwise go back in history if there is any
 *  3. a screen reached without history (deep link, cold start on a tab)
 *     returns to Home instead of leaving the app
 *  4. only Home, or the first onboarding step, exits
 */
export type BackAction = 'handled' | 'history-back' | 'go-home' | 'exit';

const ROOTS = new Set(['/', '/onboarding']);

export function decideBack({
  pathname,
  canGoBack,
  handledByScreen = false,
}: {
  pathname: string;
  canGoBack: boolean;
  handledByScreen?: boolean;
}): BackAction {
  if (handledByScreen) return 'handled';
  if (pathname === '/onboarding') return 'exit';
  if (canGoBack) return 'history-back';
  if (!ROOTS.has(pathname)) return 'go-home';
  return 'exit';
}

/** Lets the current screen claim the back press. Returns true when it did. */
export function dispatchScreenBack(): boolean {
  const event = new Event('app:back', { cancelable: true });
  window.dispatchEvent(event);
  return event.defaultPrevented;
}
